// Admin payout batch — disburse eligible merchant payouts via MonCash. Shows
// the prefunded float balance + whether the rail is live or simulated, the
// queue of eligible payouts with a one-click batch, and full history.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Wallet, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { ADMIN_NAV } from '../_navs';
import { admin, type Payout } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const FILTERS = ['all', 'pending', 'paid', 'failed'];

export function AdminPayoutsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const floatQ = useQuery({ queryKey: ['admin', 'payout-float'], queryFn: admin.payoutFloat });
  const listQ = useQuery({ queryKey: ['admin', 'payouts'], queryFn: () => admin.payouts() });

  const rows = useMemo(() => {
    const list = listQ.data ?? [];
    return filter === 'all' ? list : list.filter((p) => p.status === filter);
  }, [listQ.data, filter]);

  const pending = useMemo(() => (listQ.data ?? []).filter((p) => p.status === 'pending'), [listQ.data]);
  const pendingTotal = pending.reduce((a, p) => a + p.amount_htg_minor, 0);

  // Float guardrail: the prefunded balance must cover the pending queue.
  const floatHtgMinor = floatQ.data?.balance_htg != null ? Math.round(floatQ.data.balance_htg * 100) : null;
  const cantCover = floatHtgMinor != null && pendingTotal > 0 && floatHtgMinor < pendingTotal;
  const lowBuffer = floatHtgMinor != null && !cantCover && pendingTotal > 0 && floatHtgMinor < pendingTotal * 1.5;

  const batch = useMutation({
    mutationFn: () => admin.payoutBatch(),
    onSuccess: (res) => {
      toast.success(`${res.paid} paid · ${res.failed} failed (${res.mode})`);
      qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'payout-float'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Batch failed'),
  });

  const columns: Column<Payout>[] = [
    {
      key: 'ref', header: 'Payout', type: 'mono', width: '110px',
      value: (p) => `PO-${String(p.id).padStart(4, '0')}`,
    },
    {
      key: 'merchant', header: 'Merchant', type: 'strong', width: 'minmax(0,1.4fr)',
      value: (p) => p.merchant?.business_name ?? `#${p.merchant_id}`,
    },
    {
      key: 'order', header: 'Order', type: 'mono', width: '110px',
      value: (p) => (p.request_id ? `REQ-${String(p.request_id).padStart(4, '0')}` : '—'),
    },
    {
      key: 'method', header: 'Method', type: 'text', width: 'minmax(0,1fr)',
      value: (p) => p.method.replace('_', ' '),
    },
    {
      key: 'moncash', header: 'Reference', type: 'mono', width: 'minmax(0,1fr)',
      value: (p) => p.moncash_transaction_id ?? p.moncash_reference ?? '—',
    },
    {
      key: 'amount', header: 'Amount', type: 'money', width: '120px',
      value: (p) => formatHtgMinor(p.amount_htg_minor),
    },
    {
      key: 'status', header: 'Status', type: 'node', width: '130px',
      value: (p) => (
        <span className="inline-flex flex-col items-start gap-0.5">
          <StatusPill status={p.status} />
          {/* A failed disbursement without its reason is an unactionable
              alert — the admin needs to know whether to retry or fix a number. */}
          {p.status === 'failed' && p.moncash_message && (
            <span className="text-[10px] text-[var(--color-danger-fg)]">{p.moncash_message}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <PortalShell nav={ADMIN_NAV} activeId="payouts" title="Payouts" subtitle="Disburse eligible merchant payouts to MonCash.">
      <div className="space-y-4">
        {cantCover && (
          <div className="rounded-lg bg-[var(--color-danger-bg)] border border-[var(--color-danger-border)] px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--color-danger-fg)] mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-[var(--color-danger-fg)]">
              <strong>Float can't cover the pending queue.</strong> Prefunded balance {floatHtgMinor != null ? formatHtgMinor(floatHtgMinor) : '—'} &lt; eligible {formatHtgMinor(pendingTotal)}. Top up the Digicel prefunded account before disbursing, or only some payouts will go through.
            </p>
          </div>
        )}
        {lowBuffer && (
          <div className="rounded-lg bg-[var(--color-warning-bg)] border border-[var(--color-warning-border)] px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--color-warning-fg)] mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-[var(--color-warning-fg)]"><strong>Low float buffer.</strong> The balance covers the queue but with little headroom — schedule a top-up soon.</p>
          </div>
        )}

        {/* Float + batch */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card padding="p-3.5">
            <p className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-muted)] inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Prefunded float</p>
            <p className="mt-1 text-[19px] font-bold text-[var(--color-ink)]">{floatQ.data?.balance_htg != null ? formatHtgMinor(Math.round(floatQ.data.balance_htg * 100)) : '—'}</p>
            <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${floatQ.data?.live ? 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]' : 'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]'}`}>
              {floatQ.data?.live ? 'MonCash live' : 'Simulated'}
            </span>
          </Card>
          <Card padding="p-3.5">
            <p className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-muted)]">Eligible now</p>
            <p className="mt-1 text-[19px] font-bold text-[var(--color-warning-fg)]">{formatHtgMinor(pendingTotal)}</p>
            <p className="text-[11px] text-[var(--color-muted)]">{pending.length} payout{pending.length === 1 ? '' : 's'} pending</p>
          </Card>
          <Card padding="p-3.5" className="flex flex-col justify-center">
            <button
              onClick={() => { if (window.confirm(`Disburse ${pending.length} payout(s) — ${formatHtgMinor(pendingTotal)} — via MonCash?`)) batch.mutate(); }}
              disabled={batch.isPending || pending.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[var(--color-ink)] text-white text-[13px] font-semibold hover:bg-[var(--color-primary)] disabled:opacity-50"
            >
              {batch.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Pay all eligible
            </button>
            {floatQ.data && !floatQ.data.live && <p className="mt-1.5 text-[10.5px] text-[var(--color-warning-fg)] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Simulated until Digicel credentials are set</p>}
          </Card>
        </div>

        <DataTable
          title="Payout history"
          subtitle="Released only after the recipient enters their delivery code."
          filters={FILTERS.map((f) => ({ key: f, label: f === 'all' ? 'All' : f }))}
          activeFilter={filter}
          onFilter={(k) => setFilter(k as typeof filter)}
          columns={columns}
          rows={rows}
          rowKey={(p) => p.id}
          loading={listQ.isLoading}
          empty="No payouts yet. They appear once merchants' deliveries are confirmed."
        />
      </div>
    </PortalShell>
  );
}

